/**
 * Split.io GTM Integration
 * Pushes Split client instance to GTM dataLayer for dynamic treatment evaluation
 */

// Import Next.js third-parties types to access dataLayer declarations
import "@next/third-parties/google";

/**
 * Waits for GTM dataLayer to be available
 * @param maxAttempts - Maximum number of attempts to check for dataLayer
 * @param delay - Delay between attempts in milliseconds
 */
async function waitForGTMDataLayer(
  maxAttempts = 10,
  delay = 100,
): Promise<boolean> {
  for (let i = 0; i < maxAttempts; i++) {
    if (typeof window !== "undefined" && window.dataLayer) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
  return false;
}

/**
 * Creates and initializes a client-side Split client, then pushes it to GTM dataLayer
 * Following the Harness FME approach: https://developer.harness.io/docs/feature-management-experimentation/integrations/google-tag-manager/
 *
 * @param authorizationKey - Split client-side API key
 * @param userKey - User identifier (userId or anonymousId)
 * @returns Promise<Split client instance | null>
 */
export async function initializeSplitForGTM(
  authorizationKey: string,
  userKey: string,
): Promise<any | null> {
  if (typeof window === "undefined") {
    console.warn("[Split GTM] Cannot initialize on server-side");
    return null;
  }

  if (!authorizationKey) {
    console.warn("[Split GTM] No authorization key provided");
    return null;
  }

  try {
    // Wait for GTM dataLayer to be available
    const dataLayerReady = await waitForGTMDataLayer();

    if (!dataLayerReady) {
      console.error("[Split GTM] GTM dataLayer not available after waiting");
      return null;
    }

    // Dynamic import of Split client-side SDK
    const { SplitFactory } = await import("@splitsoftware/splitio");

    const factory = SplitFactory({
      core: {
        authorizationKey,
        key: userKey,
      },
      scheduler: {
        featuresRefreshRate: 300, // Refresh every 5 minutes
      },
      startup: {
        readyTimeout: 10, // Wait up to 10 seconds for initial data
      },
    });

    const client = factory.client();

    // Wait for client to be ready
    await client.ready();

    // Push Split client to dataLayer (Harness FME approach)
    window.dataLayer?.push({
      split_client: client,
      split_user_key: userKey,
    });

    return client;
  } catch (error) {
    console.error("[Split GTM] Failed to initialize Split client:", error);
    return null;
  }
}

/**
 * Gets the user key for Split client initialization
 * Priority: userId > anonymousId > 'anonymous'
 */
export function getSplitUserKey(userId?: string, anonymousId?: string): string {
  return userId || anonymousId || "anonymous";
}
